export const initStudioPage = ({ gsap, ScrollTrigger, lenis, reducedMotion = false } = {}) => {
    const root = document.querySelector('[data-studio-page]');
    if (!root) return;

    const sections = [...root.querySelectorAll('[data-studio-section]')];

    if (gsap && !reducedMotion) {
        sections.forEach((section) => {
            const isTeamSection = section.hasAttribute('data-studio-team-section');
            const parts = isTeamSection
                ? [...section.children].filter(Boolean).slice(0, 1)
                : [...section.children].filter(Boolean).slice(0, 2);
            const founder = section.querySelector('[data-studio-featured-member]');
            const teamHeading = section.querySelector('[data-studio-team-heading]');
            const teamGrid = section.querySelector('[data-studio-team-grid]');
            const members = [...section.querySelectorAll('[data-studio-member]')];
            const images = [...section.querySelectorAll('img')];

            if (parts.length) {
                gsap.fromTo(
                    parts,
                    { autoAlpha: 0, y: 22 },
                    {
                        autoAlpha: 1,
                        y: 0,
                        duration: 0.95,
                        stagger: 0.08,
                        ease: 'power3.out',
                        scrollTrigger: ScrollTrigger ? {
                            trigger: section,
                            start: 'top 88%',
                            once: true,
                        } : undefined,
                    },
                );
            }

            if (isTeamSection && ScrollTrigger) {
                if (founder) {
                    gsap.fromTo(
                        founder,
                        { autoAlpha: 0, y: 20 },
                        {
                            autoAlpha: 1,
                            y: 0,
                            duration: 0.88,
                            ease: 'power3.out',
                            scrollTrigger: { trigger: founder, start: 'top 90%', once: true },
                        },
                    );
                }

                if (teamHeading) {
                    gsap.fromTo(
                        teamHeading,
                        { autoAlpha: 0, y: 14 },
                        {
                            autoAlpha: 1,
                            y: 0,
                            duration: 0.68,
                            ease: 'power3.out',
                            scrollTrigger: { trigger: teamHeading, start: 'top 92%', once: true },
                        },
                    );
                }

                if (members.length) {
                    gsap.set(members, { autoAlpha: 0, y: 20 });
                    gsap.to(members, {
                        autoAlpha: 1,
                        y: 0,
                        duration: 0.74,
                        stagger: { each: 0.085, from: 'start' },
                        ease: 'power3.out',
                        scrollTrigger: {
                            trigger: teamGrid || members[0],
                            start: 'top 91%',
                            once: true,
                        },
                    });
                }
            } else {
                const cards = [...section.querySelectorAll('[data-studio-member], [data-studio-featured-member]')];
                if (cards.length > 1 && ScrollTrigger) {
                    gsap.fromTo(
                        cards,
                        { autoAlpha: 0, y: 18 },
                        {
                            autoAlpha: 1,
                            y: 0,
                            duration: 0.72,
                            stagger: 0.055,
                            ease: 'power3.out',
                            scrollTrigger: {
                                trigger: cards[0],
                                start: 'top 90%',
                                once: true,
                            },
                        },
                    );
                }
            }

            if (images.length && ScrollTrigger) {
                images.forEach((image) => {
                    gsap.fromTo(
                        image,
                        { scale: 1.035, yPercent: -1.5 },
                        {
                            scale: 1,
                            yPercent: 2.5,
                            ease: 'none',
                            scrollTrigger: {
                                trigger: image,
                                start: 'top bottom',
                                end: 'bottom top',
                                scrub: 1.2,
                            },
                        },
                    );
                });
            }
        });
    } else if (gsap) {
        gsap.set(sections, { clearProps: 'all' });
    }

    root.querySelectorAll('[data-studio-accordion]').forEach((accordion) => {
        const items = [...accordion.querySelectorAll('[data-studio-accordion-item]')];

        const refreshScroll = () => {
            lenis?.resize?.();
            window.requestAnimationFrame(() => ScrollTrigger?.refresh?.());
        };

        const closeItem = (item, immediate = false) => {
            const panel = item.querySelector('[data-studio-accordion-panel]');
            if (!panel) {
                item.open = false;
                return;
            }

            if (!gsap || reducedMotion || immediate) {
                item.open = false;
                panel.style.height = '';
                panel.style.opacity = '';
                return;
            }

            gsap.killTweensOf(panel);
            gsap.to(panel, {
                height: 0,
                autoAlpha: 0,
                duration: 0.34,
                ease: 'power3.inOut',
                overwrite: true,
                onComplete: () => {
                    item.open = false;
                    gsap.set(panel, { clearProps: 'height,opacity,visibility' });
                    refreshScroll();
                },
            });
        };

        const openItem = (item) => {
            const panel = item.querySelector('[data-studio-accordion-panel]');
            items.forEach((other) => {
                if (other !== item && other.open) closeItem(other);
            });

            if (!panel || !gsap || reducedMotion) {
                item.open = true;
                refreshScroll();
                return;
            }

            item.open = true;
            gsap.killTweensOf(panel);
            gsap.set(panel, { display: 'block', height: 0, autoAlpha: 0, overflow: 'hidden' });
            const targetHeight = panel.scrollHeight;
            gsap.to(panel, {
                height: targetHeight,
                autoAlpha: 1,
                duration: 0.48,
                ease: 'power3.inOut',
                overwrite: true,
                onComplete: () => {
                    gsap.set(panel, { clearProps: 'height,opacity,visibility,overflow' });
                    refreshScroll();
                },
            });
        };

        items.forEach((item) => {
            const summary = item.querySelector('summary');
            const panel = item.querySelector('[data-studio-accordion-panel]');
            if (!summary || !panel) return;

            if (!item.open && gsap && !reducedMotion) gsap.set(panel, { height: 0, autoAlpha: 0, overflow: 'hidden' });

            summary.addEventListener('click', (event) => {
                event.preventDefault();
                if (item.open) closeItem(item);
                else openItem(item);
            });
        });
    });
};
